import { useEffect, useState } from 'react';
import {
  AppRoot,
  Button,
  ConfigProvider,
  Placeholder,
  Spinner,
} from '@vkontakte/vkui';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useVkInit, useMe } from './api/hooks';
import { OnboardingPage } from './pages/OnboardingPage';
import { AuthGatePage } from './pages/AuthGatePage';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminParticipants } from './pages/admin/AdminParticipants';
import { AdminLicenses } from './pages/admin/AdminLicenses';
import { AdminMaps } from './pages/admin/AdminMaps';
import { AdminSimulator } from './pages/admin/AdminSimulator';
import { AdminExport } from './pages/admin/AdminExport';
import { AdminStaff } from './pages/admin/AdminStaff';
import { AdminUserFlow } from './pages/admin/AdminUserFlow';
import { api } from './api/client';
import '@vkontakte/vkui/dist/vkui.css';

const qc = new QueryClient();

function AppShell() {
  const vkInit = useVkInit();
  const queryClient = useQueryClient();
  const [ready, setReady] = useState(false);
  const { data: me, isLoading, isError, error: meError } = useMe(ready && vkInit.isSuccess);

  useEffect(() => {
    vkInit.mutate(undefined, {
      onSettled: () => setReady(true),
    });
  }, []);

  const bootError =
    vkInit.isError
      ? (vkInit.error instanceof Error ? vkInit.error.message : 'Ошибка авторизации VK')
      : isError
        ? meError instanceof Error && meError.message === 'Unauthorized'
          ? 'Сессия не сохранилась. Перезагрузите приложение. Если открываете из VK — убедитесь, что VITE_API_URL пустой в .env и dev-сервер перезапущен.'
          : 'Временные неполадки, попробуйте позже'
        : null;

  if (!ready || (ready && !bootError && isLoading)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size="l" />
      </div>
    );
  }

  if (bootError) {
    return (
      <Placeholder
        header="Не удалось запустить приложение"
        action={
          <Button size="l" onClick={() => window.location.reload()}>
            Повторить
          </Button>
        }
      >
        {bootError}
      </Placeholder>
    );
  }

  const isStaff = me?.role === 'admin' || me?.role === 'viewer';
  const isActive = me?.participant?.status === 'active';

  return (
    <Routes>
      <Route
        path="/"
        element={
          !me || !me.onboardingCompleted ? (
            <OnboardingPage
              onComplete={async () => {
                await api('/api/onboarding/complete', { method: 'POST' });
                await queryClient.invalidateQueries({ queryKey: ['me'] });
              }}
            />
          ) : isStaff ? (
            <Navigate to="/admin" replace />
          ) : isActive ? (
            <Navigate to="/profile" replace />
          ) : (
            <AuthGatePage />
          )
        }
      />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/profile"
        element={
          isStaff || isActive ? (
            <ProfilePage isStaff={isStaff} staffPreview={isStaff} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/admin"
        element={
          isStaff ? <AdminLayout role={me!.role} /> : <Navigate to="/profile" />
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="participants" element={<AdminParticipants />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="licenses" element={<AdminLicenses />} />
        <Route path="maps" element={<AdminMaps />} />
        <Route path="simulator" element={<AdminSimulator />} />
        <Route path="export" element={<AdminExport role={me?.role ?? ''} />} />
        <Route path="user-flow" element={<AdminUserFlow />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <ConfigProvider colorScheme="dark">
      <AppRoot>
        <QueryClientProvider client={qc}>
          <BrowserRouter>
            <AppShell />
          </BrowserRouter>
        </QueryClientProvider>
      </AppRoot>
    </ConfigProvider>
  );
}
